import type {
  AnalyticsPrefsUpdate,
  UsageReport,
  UsageTimeline,
  ActiveWorkspacePayload,
  BacktestListResponse,
  BacktestOverlayResponse,
  AttachmentUpdate,
  GCCVDResponse,
  GCOIResponse,
  DrawingsPayload,
  FibLevelsPayload,
  JournalAttachment,
  JournalEntriesResponse,
  JournalEntry,
  JournalEntryUpsert,
  MePayload,
  ProfileUpdate,
  AdminUsersResponse,
  AdminRolesResponse,
  ReportsListResponse,
  ReportContent,
  TradingHistoryResponse,
  SignalStatusResponse,
  StrategiesResponse,
  StrategyHistoryResponse,
  TradingOrderResult,
  TradingOrderPreview,
  PlaceOrderBody,
  ModifyOrderBody,
  CloseOrderBody,
  PendingOrderBody,
  CancelOrderBody,
  ModifyPendingBody,
  PlaceOrderOutcome,
  RiskConfig,
  OHLCRequest,
  OHLCResponse,
  RunAnalysisRequest,
  RunAnalysisResponse,
  NewsPayload,
  Note,
  NoteAttachment,
  NoteCreate,
  NotesListResponse,
  NoteUpdate,
  SessionBoundariesPayload,
  UserIndicatorsPayload,
  UserThemePayload,
  UserWeeklyBehaviourPayload,
  WorkspaceListResponse,
  WorkspacePayload,
  TraderAccess,
  ServerList,
  AdminTradersResponse,
  AdminTrader,
  EmailDraft,
  NotificationsResponse,
  ChatHistoryResponse,
  ChatInboxFilter,
  ChatInboxCounts,
  ChatInboxResponse,
  ChatInboxRoom,
  ChatMyRoomResponse,
  ChatRoomIdResponse,
  ChatTicketActionResponse,
  ChatUploadResponse,
} from "./types";
import type { SymbolsPayload } from "@/utils/symbolMeta";
import { redirectToSignIn } from "@/utils/landing";

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    /** Machine-readable discriminator from `detail.code`, when the endpoint
     *  sends one. Lets a caller tell "wait, this is still coming up" from a
     *  real failure without matching on prose. */
    public code?: string,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Base URL for the backend. Empty (default) = same-origin (the backend
 * serves the frontend). Resolved at RUNTIME from `window.__APP_CONFIG__`
 * (injected by /config.js before the bundle loads — see app/runtime_config.py)
 * so ONE build runs in any env; on split deploys it points at the API host
 * (e.g. https://api.pulsarix.net) and all requests + SSE streams + OAuth links
 * target it (the session cookie rides along via `credentials: include` + the
 * shared `.pulsarix.net` cookie domain).
 */
export const API_BASE: string = window.__APP_CONFIG__?.apiBase ?? "";

/** Prefix a backend path (e.g. "/api/load_ohlc", "/auth/google/start")
 *  with API_BASE. */
export function apiUrl(path: string): string {
  return API_BASE + path;
}

/** WebSocket URL for a backend path — same host as the API, ws/wss scheme.
 *  Used by the admin terminal's PTY socket. */
export function wsUrl(path: string): string {
  const httpBase = API_BASE || window.location.origin;
  // http -> ws, https -> wss
  return httpBase.replace(/^http/, "ws") + path;
}

async function request<TBody, TResponse>(
  path: string,
  init:
    | { method: "GET"; body?: undefined }
    | { method: "DELETE"; body?: undefined }
    | { method: "POST"; body: TBody }
    | { method: "PUT"; body: TBody }
    | { method: "PATCH"; body: TBody },
): Promise<TResponse> {
  const hasBody = init.method === "POST" || init.method === "PUT";
  const response = await fetch(apiUrl(path), {
    method: init.method,
    headers: hasBody ? { "Content-Type": "application/json" } : undefined,
    body: hasBody ? JSON.stringify(init.body) : undefined,
    // Carry the `tickview_session` cookie on every request (same-origin).
    credentials: "include",
  });

  if (!response.ok) {
    // 401 = no/expired session → bounce to the sign-in (landing) page
    // instead of surfacing an auth error. (403 is a permission denial for
    // an authenticated user — left to the caller / UI gates.)
    if (response.status === 401) {
      redirectToSignIn();
    }
    let detail = `HTTP ${response.status}`;
    let code: string | undefined;
    try {
      const body = await response.json();
      detail = body?.detail?.error ?? body?.detail ?? body?.error ?? detail;
      code = body?.detail?.code;
    } catch {
      // body wasn't JSON; keep the HTTP status fallback
    }
    throw new ApiError(response.status, String(detail), code);
  }

  return response.json() as Promise<TResponse>;
}

/** Shared POST for order placement (market or pending). Returns the
 *  discriminated outcome — the 409 risk block carries the violations
 *  array, which the generic `request` wrapper would flatten away. */
async function placeOrderFetch(
  path: string,
  body: unknown,
): Promise<PlaceOrderOutcome> {
  const resp = await fetch(apiUrl(path), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
    credentials: "include",
  });
  if (resp.status === 401) {
    redirectToSignIn();
    throw new ApiError(401, "Not authenticated");
  }
  if (resp.status === 409) {
    const data = await resp.json().catch(() => ({}));
    const detail = data?.detail ?? data ?? {};
    return {
      kind: "blocked",
      violations: detail.violations ?? [],
      message: detail.message ?? "Blocked by a risk control",
    };
  }
  if (!resp.ok) {
    let detail = `HTTP ${resp.status}`;
    try {
      const b = await resp.json();
      detail = b?.detail?.error ?? b?.detail ?? b?.error ?? detail;
    } catch {
      // body wasn't JSON
    }
    throw new ApiError(resp.status, String(detail));
  }
  const result = (await resp.json()) as TradingOrderResult;
  return { kind: result.ok ? "filled" : "rejected", result };
}

/** Terminal-flow fetch — like `request` but a 401 means "wrong factor",
 *  NOT "session expired", so it must NOT bounce to sign-in. Throws ApiError
 *  for any non-2xx so the modal can show the failure inline. */
async function terminalRequest<TResponse>(
  path: string,
  method: "GET" | "POST" | "DELETE",
  body?: unknown,
): Promise<TResponse> {
  const resp = await fetch(apiUrl(path), {
    method,
    headers: method === "POST" ? { "Content-Type": "application/json" } : undefined,
    body: method === "POST" ? JSON.stringify(body ?? {}) : undefined,
    credentials: "include",
  });
  if (!resp.ok) {
    let detail = `HTTP ${resp.status}`;
    try {
      const b = await resp.json();
      detail = b?.detail?.error ?? b?.detail ?? b?.error ?? detail;
    } catch {
      // not JSON
    }
    throw new ApiError(resp.status, String(detail));
  }
  return resp.json() as Promise<TResponse>;
}

export const api = {
  loadOhlc: (req: OHLCRequest) =>
    request<OHLCRequest, OHLCResponse>("/api/load_ohlc", {
      method: "POST",
      body: req,
    }),

  runAnalysis: (req: RunAnalysisRequest) =>
    request<RunAnalysisRequest, RunAnalysisResponse>("/api/run_analysis", {
      method: "POST",
      body: req,
    }),

  health: () =>
    request<undefined, { status: string; service: string }>("/api/health", {
      method: "GET",
    }),

  getMe: () =>
    request<undefined, MePayload>("/api/me", { method: "GET" }),

  // Guided tours. Marking is fire-and-forget from the caller's point of view
  // but returns the refreshed /api/me payload so the slice stays in step.
  markTourSeen: (tourId: string) =>
    request<undefined, MePayload>(`/api/me/tours/${encodeURIComponent(tourId)}`, {
      method: "POST",
      body: undefined,
    }),

  resetTours: () =>
    request<undefined, MePayload>("/api/me/tours", { method: "DELETE" }),

  // Usage tab reports (usage.view). Dates are YYYY-MM-DD, UTC.
  adminUsage: (start: string, end: string, includeAdmins: boolean) =>
    request<undefined, UsageReport>(
      `/api/admin/usage?start=${start}&end=${end}&exclude_admins=${!includeAdmins}`,
      { method: "GET" },
    ),

  adminUsageUser: (sub: string, start: string, end: string) =>
    request<undefined, UsageTimeline>(
      `/api/admin/usage/users/${encodeURIComponent(sub)}?start=${start}&end=${end}`,
      { method: "GET" },
    ),

  // Usage analytics opt-out + acknowledging the usage-data notice.
  setAnalyticsPrefs: (body: AnalyticsPrefsUpdate) =>
    request<AnalyticsPrefsUpdate, MePayload>("/api/me/analytics", {
      method: "PUT",
      body,
    }),

  // --- Trader self-service onboarding ---
  // What the single Connect MT5 button should do for this user. The server
  // returns an `action`; the client never derives it from the role.
  traderAccess: () =>
    request<undefined, TraderAccess>("/api/trading/access", { method: "GET" }),

  traderRequestAccess: (body: { broker: string; note: string }) =>
    request<{ broker: string; note: string }, TraderAccess & { ok: boolean }>(
      "/api/trading/access/request",
      { method: "POST", body },
    ),

  traderServers: () =>
    request<undefined, ServerList>("/api/trading/servers", { method: "GET" }),

  // The ONLY call that carries a broker password. It is relayed once to this
  // user's own MT5 worker and stored nowhere — see per_user_mt5_selfserve.md.
  traderConnectMt5: (body: { login: number; password: string; server: string }) =>
    request<typeof body, TraderAccess & { ok: boolean }>(
      "/api/trading/mt5/connect",
      { method: "POST", body },
    ),

  traderDisconnectMt5: () =>
    request<undefined, TraderAccess & { ok: boolean }>(
      "/api/trading/mt5/disconnect",
      { method: "POST", body: undefined },
    ),

  // --- Admin: trader review (traders.manage) ---
  adminTraders: () =>
    request<undefined, AdminTradersResponse>("/api/admin/traders", {
      method: "GET",
    }),

  adminTraderApprove: (sub: string) =>
    request<undefined, { ok: boolean; trader: AdminTrader }>(
      `/api/admin/traders/${encodeURIComponent(sub)}/approve`,
      { method: "POST", body: undefined },
    ),

  adminTraderReject: (sub: string, reason: string) =>
    request<{ reason: string }, { ok: boolean; trader: AdminTrader }>(
      `/api/admin/traders/${encodeURIComponent(sub)}/reject`,
      { method: "POST", body: { reason } },
    ),

  adminTraderRevoke: (sub: string, reason: string) =>
    request<{ reason: string }, { ok: boolean; trader: AdminTrader }>(
      `/api/admin/traders/${encodeURIComponent(sub)}/revoke`,
      { method: "POST", body: { reason } },
    ),

  // Close a trader's MT5 terminal; they keep access and simply log in again.
  adminTraderCloseTerminal: (sub: string) =>
    request<undefined, { ok: boolean; trader: AdminTrader }>(
      `/api/admin/traders/${encodeURIComponent(sub)}/close-terminal`,
      { method: "POST", body: undefined },
    ),
  adminTraderReprovision: (sub: string) =>
    request<undefined, { ok: boolean; trader: AdminTrader }>(
      `/api/admin/traders/${encodeURIComponent(sub)}/reprovision`,
      { method: "POST", body: undefined },
    ),

  // Approval emails the operator still has to send by hand.
  adminEmailDrafts: () =>
    request<undefined, { drafts: EmailDraft[]; auto_send: boolean }>(
      "/api/admin/traders/drafts",
      { method: "GET" },
    ),

  adminMarkDraftSent: (id: string) =>
    request<undefined, { ok: boolean; pending_drafts: number }>(
      `/api/admin/traders/drafts/${encodeURIComponent(id)}/sent`,
      { method: "POST", body: undefined },
    ),

  // --- Notifications ---
  notifications: () =>
    request<undefined, NotificationsResponse>("/api/notifications", {
      method: "GET",
    }),

  notificationRead: (id: string) =>
    request<undefined, { ok: boolean; unread: number }>(
      `/api/notifications/${encodeURIComponent(id)}/read`,
      { method: "POST", body: undefined },
    ),

  notificationsReadAll: () =>
    request<undefined, { ok: boolean; marked: number; unread: number }>(
      "/api/notifications/read_all",
      { method: "POST", body: undefined },
    ),

  // Master-only management dashboard: the registered-user roster + presence.
  adminUsers: () =>
    request<undefined, AdminUsersResponse>("/api/admin/users", {
      method: "GET",
    }),

  // Replace a user's RBAC role set (durable; [] = viewer). 409 if the target
  // is an admin via server config (env) — can't be changed here.
  adminSetRoles: (sub: string, roles: string[]) =>
    request<{ roles: string[] }, { ok: boolean; sub: string; roles: string[] }>(
      `/api/admin/users/${encodeURIComponent(sub)}/roles`,
      { method: "POST", body: { roles } },
    ),

  // The fixed roles + role→capability matrix (read-only).
  adminRoles: () =>
    request<undefined, AdminRolesResponse>("/api/admin/roles", {
      method: "GET",
    }),

  // Admin research report viewer (reports.view). List metadata, then fetch one
  // report's raw markdown (rendered client-side).
  adminReports: () =>
    request<undefined, ReportsListResponse>("/api/admin/reports", {
      method: "GET",
    }),

  adminReport: (name: string) =>
    request<undefined, ReportContent>(
      `/api/admin/reports/${encodeURIComponent(name)}`,
      { method: "GET" },
    ),

  // Force sign-out: delete all of a user's sessions.
  adminRevokeSessions: (sub: string) =>
    request<Record<string, never>, { ok: boolean; sub: string; revoked: number }>(
      `/api/admin/users/${encodeURIComponent(sub)}/revoke`,
      { method: "POST", body: {} },
    ),

  // Presence keep-alive. Bodyless POST (the typed `request` wrapper needs a
  // body); best-effort — failures are swallowed so they never disrupt the UI.
  // `leaving` is the parting ping as the page is hidden or closed: it makes
  // the server stamp "last active" at that moment, and rides the same
  // keepalive path as the analytics queue so it survives the unload
  // (sendBeacon can't send JSON cross-origin in the split deploy).
  heartbeat: async (opts?: { leaving?: boolean }): Promise<void> => {
    try {
      await fetch(apiUrl("/api/heartbeat"), {
        method: "POST",
        credentials: "include",
        ...(opts?.leaving
          ? {
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ leaving: true }),
              keepalive: true,
            }
          : {}),
      });
    } catch {
      /* offline / transient — ignore */
    }
  },

  // One-time onboarding profile submit. Returns the refreshed /api/me
  // payload (now profile_complete: true).
  saveProfile: (payload: ProfileUpdate) =>
    request<ProfileUpdate, MePayload>("/api/profile", {
      method: "PUT",
      body: payload,
    }),

  // Bodyless POST — bypass the typed `request` wrapper (which requires a
  // POST body). Best-effort; the caller reloads regardless.
  signout: async (): Promise<void> => {
    await fetch(apiUrl("/auth/signout"), {
      method: "POST",
      credentials: "include",
    });
  },

  // the trading data — owner-gated server-side. Live updates for
  // account / positions / orders come via SSE (useTradingStream); the
  // History tab is a one-shot REST fetch because the date range is
  // user-selected and changes on demand.
  /** Closed-trade history. With `days` omitted the server returns
   *  ALL cached history (the on-disk cache holds every trade for
   *  this the trading server account, delta-synced on each call). Pass a number
   *  to filter to the last N days. */
  tradingHistory: (days?: number, symbol?: string) => {
    const params: string[] = [];
    if (days != null) params.push(`days=${days}`);
    if (symbol) params.push(`symbol=${encodeURIComponent(symbol)}`);
    const qs = params.length ? `?${params.join("&")}` : "";
    return request<undefined, TradingHistoryResponse>(
      `/api/trading/history${qs}`,
      { method: "GET" },
    );
  },

  // --- signal chart (shared read-only bot_all reference; all signed-in users) ---

  /** Whether the signal chart is enabled + the source is online + its symbol. */
  signalStatus: () =>
    request<undefined, SignalStatusResponse>("/api/signal/status", {
      method: "GET",
    }),

  /** bot_all closed-trade history for the pane's entry/exit markers. */
  signalHistory: (days?: number) => {
    const qs = days != null ? `?days=${days}` : "";
    return request<undefined, TradingHistoryResponse>(
      `/api/signal/history${qs}`,
      { method: "GET" },
    );
  },

  // --- strategy signals (shared strategy runner; all signed-in users, read-only) ---

  /** Every visible strategy with its live status, exposure and 30-day stats. */
  strategies: () =>
    request<undefined, StrategiesResponse>("/api/strategies", { method: "GET" }),

  /** One strategy's trades (entry/exit markers on the strategies chart). */
  strategyHistory: (id: string, days?: number) => {
    const qs = days != null ? `?days=${days}` : "";
    return request<undefined, StrategyHistoryResponse>(
      `/api/strategies/${encodeURIComponent(id)}/history${qs}`,
      { method: "GET" },
    );
  },

  // --- the trading server order placement (Phase-1 live trading; master-gated) ---

  /** Dry-run the risk gate for the trade-ticket modal's pre-trade row.
   *  Returns the broker numbers + verdict without placing anything. */
  tradingOrderPreview: (body: PlaceOrderBody) =>
    request<PlaceOrderBody, TradingOrderPreview>("/api/trading/order/preview", {
      method: "POST",
      body,
    }),

  /** Place a market order. Returns a discriminated outcome so the caller
   *  can branch on filled / broker-rejected / risk-blocked without
   *  string-matching. The 409 risk block carries the violations array,
   *  which the generic `request` wrapper would flatten away — so this
   *  does its own fetch. */
  tradingPlaceOrder: (body: PlaceOrderBody): Promise<PlaceOrderOutcome> =>
    placeOrderFetch("/api/trading/order", body),

  /** Place a pending order (buy/sell limit/stop). Same discriminated
   *  outcome as market placement (can be risk-blocked → 409). */
  tradingPlacePending: (body: PendingOrderBody): Promise<PlaceOrderOutcome> =>
    placeOrderFetch("/api/trading/order/pending", body),

  tradingCancelOrder: (body: CancelOrderBody) =>
    request<CancelOrderBody, TradingOrderResult>("/api/trading/order/cancel", {
      method: "POST",
      body,
    }),

  tradingModifyPending: (body: ModifyPendingBody) =>
    request<ModifyPendingBody, TradingOrderResult>("/api/trading/order/modify_pending", {
      method: "POST",
      body,
    }),

  tradingModifyOrder: (body: ModifyOrderBody) =>
    request<ModifyOrderBody, TradingOrderResult>("/api/trading/order/modify", {
      method: "POST",
      body,
    }),

  tradingCloseOrder: (body: CloseOrderBody) =>
    request<CloseOrderBody, TradingOrderResult>("/api/trading/order/close", {
      method: "POST",
      body,
    }),

  tradingGetRiskConfig: () =>
    request<undefined, RiskConfig>("/api/trading/risk_config", { method: "GET" }),

  tradingSaveRiskConfig: (patch: Partial<RiskConfig>) =>
    request<Partial<RiskConfig>, RiskConfig>("/api/trading/risk_config", {
      method: "PUT",
      body: patch,
    }),

  getFibLevels: () =>
    request<undefined, FibLevelsPayload>("/api/fib_levels", { method: "GET" }),

  saveFibLevels: (payload: FibLevelsPayload) =>
    request<FibLevelsPayload, FibLevelsPayload>("/api/fib_levels", {
      method: "PUT",
      body: payload,
    }),

  getUserTheme: () =>
    request<undefined, UserThemePayload>("/api/user_theme", { method: "GET" }),

  saveUserTheme: (payload: UserThemePayload) =>
    request<UserThemePayload, UserThemePayload>("/api/user_theme", {
      method: "PUT",
      body: payload,
    }),

  getUserWeeklyBehaviour: () =>
    request<undefined, UserWeeklyBehaviourPayload>(
      "/api/user_weekly_behaviour",
      { method: "GET" },
    ),

  saveUserWeeklyBehaviour: (payload: UserWeeklyBehaviourPayload) =>
    request<UserWeeklyBehaviourPayload, UserWeeklyBehaviourPayload>(
      "/api/user_weekly_behaviour",
      { method: "PUT", body: payload },
    ),

  getUserIndicators: () =>
    request<undefined, UserIndicatorsPayload>("/api/indicators", {
      method: "GET",
    }),

  saveUserIndicators: (payload: UserIndicatorsPayload) =>
    request<UserIndicatorsPayload, UserIndicatorsPayload>("/api/indicators", {
      method: "PUT",
      body: payload,
    }),

  getSessionBoundaries: (symbol: string) =>
    request<undefined, SessionBoundariesPayload>(
      `/api/session_boundaries/${encodeURIComponent(symbol)}`,
      { method: "GET" },
    ),

  getSymbols: () =>
    request<undefined, SymbolsPayload>("/api/symbols", { method: "GET" }),

  getDrawings: (symbol: string) =>
    request<undefined, DrawingsPayload>(
      `/api/drawings?symbol=${encodeURIComponent(symbol)}`,
      { method: "GET" },
    ),

  listBacktests: (symbol: string) =>
    request<undefined, BacktestListResponse>(
      `/api/backtest?symbol=${encodeURIComponent(symbol)}`,
      { method: "GET" },
    ),

  getBacktestOverlay: (symbol: string, name: string) =>
    request<undefined, BacktestOverlayResponse>(
      `/api/backtest/overlay?symbol=${encodeURIComponent(symbol)}&name=${encodeURIComponent(name)}`,
      { method: "GET" },
    ),

  saveDrawings: (symbol: string, payload: DrawingsPayload) =>
    request<DrawingsPayload, DrawingsPayload>(
      `/api/drawings?symbol=${encodeURIComponent(symbol)}`,
      { method: "PUT", body: payload },
    ),

  listWorkspaces: () =>
    request<undefined, WorkspaceListResponse>("/api/workspaces", {
      method: "GET",
    }),

  getActiveWorkspace: () =>
    request<undefined, ActiveWorkspacePayload>("/api/workspaces/active", {
      method: "GET",
    }),

  setActiveWorkspace: (payload: ActiveWorkspacePayload) =>
    request<ActiveWorkspacePayload, ActiveWorkspacePayload>(
      "/api/workspaces/active",
      { method: "PUT", body: payload },
    ),

  getWorkspace: (name: string) =>
    request<undefined, WorkspacePayload>(
      `/api/workspaces/${encodeURIComponent(name)}`,
      { method: "GET" },
    ),

  saveWorkspace: (name: string, payload: WorkspacePayload) =>
    request<WorkspacePayload, WorkspacePayload>(
      `/api/workspaces/${encodeURIComponent(name)}`,
      { method: "PUT", body: payload },
    ),

  deleteWorkspace: (name: string) =>
    request<undefined, { deleted: string }>(
      `/api/workspaces/${encodeURIComponent(name)}`,
      { method: "DELETE" },
    ),

  // --- Trade Journal (owner-gated) ---
  journalList: () =>
    request<undefined, JournalEntriesResponse>("/api/journal/entries", {
      method: "GET",
    }),

  journalUpsert: (key: string, payload: JournalEntryUpsert) =>
    request<JournalEntryUpsert, JournalEntry>(
      `/api/journal/entries/${encodeURIComponent(key)}`,
      { method: "PUT", body: payload },
    ),

  journalDelete: (key: string) =>
    request<undefined, { deleted: string }>(
      `/api/journal/entries/${encodeURIComponent(key)}`,
      { method: "DELETE" },
    ),

  journalRekey: (key: string, newKey: string) =>
    request<undefined, JournalEntry>(
      `/api/journal/entries/${encodeURIComponent(key)}/rekey?new_key=${encodeURIComponent(newKey)}`,
      { method: "POST", body: undefined as unknown as undefined },
    ),

  /** Uploads a PNG blob as multipart/form-data. Returns the
   *  attachment metadata (id, filename, bytes, uploaded_at). The
   *  raw image is fetched separately via /api/journal/attachments/<id>
   *  using a plain <img src=...>; no need to round-trip through JS. */
  journalUploadAttachment: async (
    key: string,
    blob: Blob,
    filename: string,
    /** Reuse an existing image's label instead of minting the next
     *  number. The annotator passes the original label through so an
     *  edited image keeps the number the notes already cite. */
    label?: string,
    /** Timeframe of the chart this came from, MT5 spelling ("H1").
     *  Omitted for a file off disk — the server then applies its own
     *  generic prefix. */
    prefix?: string,
  ): Promise<JournalAttachment> => {
    const form = new FormData();
    form.append("file", blob, filename);
    if (label) form.append("label", label);
    if (prefix) form.append("prefix", prefix);
    const resp = await fetch(
      apiUrl(`/api/journal/entries/${encodeURIComponent(key)}/attachments`),
      { method: "POST", body: form, credentials: "include" },
    );
    if (!resp.ok) {
      if (resp.status === 401) {
        redirectToSignIn();
      }
      let detail = `HTTP ${resp.status}`;
      try {
        const body = await resp.json();
        detail = body?.detail?.error ?? body?.detail ?? body?.error ?? detail;
      } catch {
        // body wasn't JSON
      }
      throw new ApiError(resp.status, String(detail));
    }
    return (await resp.json()) as JournalAttachment;
  },

  /** Rename an attachment. A label cleared to blank comes back as the
   *  next free number — the server never stores an empty label. */
  journalUpdateAttachment: (
    key: string,
    attId: string,
    payload: AttachmentUpdate,
  ) =>
    request<AttachmentUpdate, JournalAttachment>(
      `/api/journal/entries/${encodeURIComponent(key)}/attachments/${encodeURIComponent(attId)}`,
      { method: "PATCH", body: payload },
    ),

  journalDeleteAttachment: (key: string, attId: string) =>
    request<undefined, { deleted: string }>(
      `/api/journal/entries/${encodeURIComponent(key)}/attachments/${encodeURIComponent(attId)}`,
      { method: "DELETE" },
    ),

  // --- Economic news calendar (current week; ForexFactory + FTMO) ---
  // One payload carries BOTH sources, each event tagged with its `source`,
  // so the panel's source / currency / impact / restricted filters all run
  // client-side with no refetch.
  news: () => request<undefined, NewsPayload>("/api/news", { method: "GET" }),

  /** Force an upstream refetch (the server also reconciles on a loop). */
  newsRefresh: () =>
    request<Record<string, never>, NewsPayload>("/api/news/refresh", {
      method: "POST",
      body: {},
    }),

  // --- Notes (freeform notebook, owner-gated) ---
  notesList: () =>
    request<undefined, NotesListResponse>("/api/notes", { method: "GET" }),

  notesCreate: (payload: NoteCreate) =>
    request<NoteCreate, Note>("/api/notes", {
      method: "POST",
      body: payload,
    }),

  notesUpdate: (id: string, payload: NoteUpdate) =>
    request<NoteUpdate, Note>(`/api/notes/${encodeURIComponent(id)}`, {
      method: "PUT",
      body: payload,
    }),

  notesDelete: (id: string) =>
    request<undefined, { deleted: string }>(
      `/api/notes/${encodeURIComponent(id)}`,
      { method: "DELETE" },
    ),

  notesUploadAttachment: async (
    id: string,
    blob: Blob,
    filename: string,
    /** Reuse an existing image's label instead of minting the next
     *  number. The annotator passes the original label through so an
     *  edited image keeps the number the body already cites. */
    label?: string,
    /** Timeframe of the chart this came from, MT5 spelling ("H1").
     *  Omitted for a file off disk — the server then applies its own
     *  generic prefix. */
    prefix?: string,
  ): Promise<NoteAttachment> => {
    const form = new FormData();
    form.append("file", blob, filename);
    if (label) form.append("label", label);
    if (prefix) form.append("prefix", prefix);
    const resp = await fetch(
      apiUrl(`/api/notes/${encodeURIComponent(id)}/attachments`),
      { method: "POST", body: form, credentials: "include" },
    );
    if (!resp.ok) {
      if (resp.status === 401) {
        redirectToSignIn();
      }
      let detail = `HTTP ${resp.status}`;
      try {
        const body = await resp.json();
        detail = body?.detail?.error ?? body?.detail ?? body?.error ?? detail;
      } catch {
        // body wasn't JSON
      }
      throw new ApiError(resp.status, String(detail));
    }
    return (await resp.json()) as NoteAttachment;
  },

  /** Rename an attachment. A label cleared to blank comes back as the
   *  next free number — the server never stores an empty label. */
  notesUpdateAttachment: (
    id: string,
    attId: string,
    payload: AttachmentUpdate,
  ) =>
    request<AttachmentUpdate, NoteAttachment>(
      `/api/notes/${encodeURIComponent(id)}/attachments/${encodeURIComponent(attId)}`,
      { method: "PATCH", body: payload },
    ),

  notesDeleteAttachment: (id: string, attId: string) =>
    request<undefined, { deleted: string }>(
      `/api/notes/${encodeURIComponent(id)}/attachments/${encodeURIComponent(attId)}`,
      { method: "DELETE" },
    ),

  // --- Admin terminal (admin-only; gated server-side, 404 when off) ---
  // All terminal calls use terminalRequest so a bad-factor 401 shows inline
  // instead of bouncing to sign-in.
  terminalEnrollStatus: () =>
    terminalRequest<{
      password: boolean;
      totp: boolean;
      webauthn: boolean;
      fully_enrolled: boolean;
    }>("/api/admin/terminal/enroll/status", "GET"),

  // Passkey registration (step-up: existing password + TOTP).
  terminalEnrollBegin: (password: string, totp: string) =>
    terminalRequest<{ options: unknown }>(
      "/api/admin/terminal/enroll/passkey/begin",
      "POST",
      { password, totp },
    ),

  terminalEnrollFinish: (registration: unknown) =>
    terminalRequest<{ ok: boolean; fully_enrolled: boolean }>(
      "/api/admin/terminal/enroll/passkey/finish",
      "POST",
      { registration },
    ),

  // Per-session status on the host. Drives the Resume-vs-New choice.
  terminalSession: () =>
    terminalRequest<{ host_up: boolean; sessions: Record<string, boolean>; max_tabs: number }>(
      "/api/admin/terminal/session",
      "GET",
    ),

  // Kill a specific PTY session (operator tab close).
  terminalKillSession: (sessionId: string) =>
    terminalRequest<{ ok: boolean }>(
      `/api/admin/terminal/session/${encodeURIComponent(sessionId)}`,
      "DELETE",
    ),

  // Open flow — each step holds the flow_id, same session+ip.
  terminalOpenBegin: () =>
    terminalRequest<{ flow_id: string; passkey_options: unknown }>(
      "/api/admin/terminal/open/begin",
      "POST",
    ),

  terminalOpenCredentials: (flowId: string, password: string, passkey: unknown) =>
    terminalRequest<{ ok: boolean; code_sent: boolean }>(
      "/api/admin/terminal/open/credentials",
      "POST",
      { flow_id: flowId, password, passkey },
    ),

  // Phase 8: returns ws_tokens keyed by session ("1"/"2"/"3") + reconnect_token.
  terminalOpenCodes: (flowId: string, totp: string, telegramCode: string) =>
    terminalRequest<{ ws_tokens: Record<string, string>; reconnect_token: string; ttl: number }>(
      "/api/admin/terminal/open/codes",
      "POST",
      { flow_id: flowId, totp, telegram_code: telegramCode },
    ),

  // Phase 7: skip the full gauntlet using the session-bound reconnect token.
  terminalReconnect: (reconnect_token: string) =>
    terminalRequest<{ ws_tokens: Record<string, string>; ttl: number }>(
      "/api/admin/terminal/reconnect",
      "POST",
      { reconnect_token },
    ),

  // --- GC futures panels ---
  // Per-bar delta for one contract. Never merged across contracts: GCQ6 and
  // GCZ6 are separate order books and cumulating across a roll invents flow.
  gcCvd: (p: {
    contract: string;
    timeframe: string;
    start: string;
    end: string;
  }) =>
    request<undefined, GCCVDResponse>(
      `/api/gc/cvd?contract=${encodeURIComponent(p.contract)}` +
        `&timeframe=${encodeURIComponent(p.timeframe)}` +
        `&start=${encodeURIComponent(p.start)}&end=${encodeURIComponent(p.end)}`,
      { method: "GET" },
    ),

  // Total open interest across every expiry, one point per session.
  gcOi: (p: { start?: string; end?: string } = {}) => {
    const q = new URLSearchParams();
    if (p.start) q.set("start", p.start);
    if (p.end) q.set("end", p.end);
    const qs = q.toString();
    return request<undefined, GCOIResponse>(
      `/api/gc/oi${qs ? `?${qs}` : ""}`,
      { method: "GET" },
    );
  },

  // --- Chat (docs/rfcs/2026-09-19-realtime-chat-public-private.md) ---
  // Live traffic is on the WebSocket (hooks/useChatSocket); these are the
  // REST reads and ticket actions. Room access is enforced server-side.

  /** The signed-in user's own support room (created on first use) and
   *  their unread count in it. */
  chatMyRoom: () =>
    request<undefined, ChatMyRoomResponse>("/api/chat/rooms/me", {
      method: "GET",
    }),

  /** The community room (created on first use). */
  /** The user's unread support replies WITHOUT creating their room (unlike
   *  chatMyRoom): `room_id` null and 0 when they have none yet. */
  chatMyUnread: () =>
    request<undefined, { room_id: string | null; unread_count: number }>(
      "/api/chat/rooms/me/unread",
      { method: "GET" },
    ),

  chatPublicRoom: () =>
    request<undefined, ChatRoomIdResponse>("/api/chat/rooms/public", {
      method: "GET",
    }),

  /** One page of history, newest first. Omit `beforeId` for the latest
   *  page. Also marks the other side's messages read (direct rooms). */
  chatHistory: (roomId: string, beforeId?: number) => {
    const qs = beforeId != null ? `?before_id=${beforeId}` : "";
    return request<undefined, ChatHistoryResponse>(
      `/api/chat/rooms/${encodeURIComponent(roomId)}/history${qs}`,
      { method: "GET" },
    );
  },

  /** Supporter inbox page (chat.support). `cursor` is the previous page's
   *  `next_cursor`, passed back verbatim — URLSearchParams percent-encodes
   *  its `+00:00`, which the server would otherwise read as a space. */
  chatInbox: (filter: ChatInboxFilter, cursor?: string | null) => {
    const q = new URLSearchParams({ filter });
    if (cursor) q.set("cursor", cursor);
    return request<undefined, ChatInboxResponse>(`/api/chat/inbox?${q}`, {
      method: "GET",
    });
  },

  /** Rooms per inbox filter (chat.support) — summary cards, filter tabs,
   *  nav badge. Counts everything, not just the loaded page. */
  chatInboxCounts: () =>
    request<undefined, ChatInboxCounts>("/api/chat/inbox/counts", { method: "GET" }),

  /** "I'm viewing this room": marks the other side's messages read for the
   *  caller, up to `upToId` — the newest message the client is showing.
   *  Returns no messages — see chatSlice's markChatRoomRead. */
  chatMarkRead: (roomId: string, upToId: number) =>
    request<{ up_to_id: number }, { ok: boolean }>(
      `/api/chat/rooms/${encodeURIComponent(roomId)}/read`,
      { method: "POST", body: { up_to_id: upToId } },
    ),

  /** One support ticket in the inbox row shape (chat.support), whatever
   *  filter it falls under. 404 for a room that isn't a ticket. */
  chatTicket: (roomId: string) =>
    request<undefined, ChatInboxRoom>(`/api/chat/rooms/${encodeURIComponent(roomId)}`, {
      method: "GET",
    }),

  /** Claim / resolve / unclaim a ticket (chat.support). A failed guard is a
   *  409 ApiError — refresh the inbox rather than retrying. */
  chatClaim: (roomId: string) =>
    request<Record<string, never>, ChatTicketActionResponse>(
      `/api/chat/rooms/${encodeURIComponent(roomId)}/claim`,
      { method: "POST", body: {} },
    ),

  chatResolve: (roomId: string) =>
    request<Record<string, never>, ChatTicketActionResponse>(
      `/api/chat/rooms/${encodeURIComponent(roomId)}/resolve`,
      { method: "POST", body: {} },
    ),

  chatUnclaim: (roomId: string) =>
    request<Record<string, never>, ChatTicketActionResponse>(
      `/api/chat/rooms/${encodeURIComponent(roomId)}/unclaim`,
      { method: "POST", body: {} },
    ),

  /** `<img src>` for an image message. Its `content` is the served PATH
   *  (`/api/chat/rooms/{room}/uploads/{file}`), so it must go through
   *  `apiUrl()` — bare, a split deploy would resolve it against the
   *  frontend's origin. The cookie rides along (same-site). */
  chatImageUrl: (content: string): string => apiUrl(content),

  /** Upload an image (JPEG/PNG/WebP, ≤5 MB, 5/min). Creates no message —
   *  send the returned `content` + `original_filename` as an image
   *  `send_message` on the socket. */
  chatUploadImage: async (
    roomId: string,
    blob: Blob,
    filename: string,
  ): Promise<ChatUploadResponse> => {
    const form = new FormData();
    form.append("file", blob, filename);
    const resp = await fetch(
      apiUrl(`/api/chat/rooms/${encodeURIComponent(roomId)}/upload`),
      { method: "POST", body: form, credentials: "include" },
    );
    if (!resp.ok) {
      if (resp.status === 401) {
        redirectToSignIn();
      }
      let detail = `HTTP ${resp.status}`;
      try {
        const body = await resp.json();
        detail = body?.detail?.error ?? body?.detail ?? body?.error ?? detail;
      } catch {
        // body wasn't JSON
      }
      throw new ApiError(resp.status, String(detail));
    }
    return (await resp.json()) as ChatUploadResponse;
  },
};

